import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { HandHeart } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const HELP_EMOJI = {
  donate_money: "💰", sponsor_scholarship: "🎓", donate_books: "📚", donate_technology: "💻",
  donate_equipment: "📦", volunteer: "🤝", mentor: "👨‍🏫", internship: "💼", employment: "💼",
  research_support: "🔬", campus_project: "🏫", guest_lecture: "🎤", expertise: "💡",
  introduction: "🌎", community_project: "🌎", student_support: "❤️", sponsor_student: "❤️",
  service: "🛠️", other: "💬",
};

// "Help Your Alma Mater" dialog — pick a way to help + submit an offer/pledge.
export default function HelpAlmaMaterDialog({ instId, instName, projectId = null, projectTitle = "",
  label = "Help Your Alma Mater", variant = "default", size = "default", triggerClassName = "", onDone }) {
  const [open, setOpen] = useState(false);
  const [helpTypes, setHelpTypes] = useState([]);
  const [step, setStep] = useState(1);
  const [helpType, setHelpType] = useState("");
  const [amount, setAmount] = useState("");
  const [quantity, setQuantity] = useState("");
  const [message, setMessage] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && helpTypes.length === 0) {
      api.get("/impact/taxonomy").then((r) => setHelpTypes(r.data.help_types || [])).catch(() => {});
    }
  }, [open, helpTypes.length]);

  const reset = () => { setStep(1); setHelpType(""); setAmount(""); setQuantity(""); setMessage(""); setVisibility("public"); setAnonymous(false); };
  const isMoney = ["donate_money", "sponsor_scholarship"].includes(helpType);

  const submit = async () => {
    if (!helpType) { toast.error("Please choose how you'd like to help"); return; }
    setBusy(true);
    try {
      await api.post(`/institutions/${instId}/contribute`, {
        help_type: helpType, project_id: projectId, message,
        amount: isMoney ? parseFloat(amount || "0") : 0, quantity,
        visibility, anonymous,
      });
      toast.success("Thank you! Your offer was sent to the institution to confirm.");
      setOpen(false); reset(); onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className={`gap-1.5 ${triggerClassName}`} data-testid="help-alma-mater-btn">
          <HandHeart size={16} weight="duotone" /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="help-alma-mater-dialog">
        <DialogHeader>
          <DialogTitle>Help {instName}</DialogTitle>
          <DialogDescription>
            {projectTitle ? <>For project: <span className="font-medium">{projectTitle}</span>. </> : null}
            Every form of help counts — time, knowledge, mentoring, equipment or funds.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="help-type-grid">
            {helpTypes.map((h) => (
              <button
                key={h.key}
                onClick={() => { setHelpType(h.key); setStep(2); }}
                data-testid={`help-type-${h.key}`}
                className={`flex flex-col items-center gap-1 rounded-xl border p-3 text-center transition-all hover:border-secondary hover:bg-secondary/5 ${helpType === h.key ? "border-secondary bg-secondary/10" : "border-border/60"}`}
              >
                <span className="text-xl">{HELP_EMOJI[h.key] || "💬"}</span>
                <span className="text-xs font-medium leading-tight">{h.label}</span>
              </button>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-lg bg-secondary/10 px-3 py-2 text-sm">
              <span className="text-lg">{HELP_EMOJI[helpType]}</span>
              <span className="font-medium">{helpTypes.find((h) => h.key === helpType)?.label}</span>
              <button className="ml-auto text-xs text-secondary hover:underline" onClick={() => setStep(1)}>Change</button>
            </div>
            {isMoney && (
              <div className="space-y-1.5">
                <Label>Amount you'd like to pledge (no card charged — the institution will contact you)</Label>
                <Input type="number" min="0" placeholder="e.g. 500" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="help-amount" />
                <p className="text-xs text-muted-foreground">AlumniPulse is a facilitator only — we never handle or transfer funds. You'll arrange any payment directly with the institution.</p>
              </div>
            )}
            {!isMoney && (
              <div className="space-y-1.5">
                <Label>What can you offer? (optional)</Label>
                <Input placeholder="e.g. 10 computers · 2 hours/month · 3 internships" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="help-quantity" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>A short message to the institution</Label>
              <Textarea rows={3} placeholder="Tell them how you'd like to help…" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="help-message" />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Who can see this on your profile?</Label>
                <Select value={visibility} onValueChange={setVisibility}>
                  <SelectTrigger data-testid="help-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public</SelectItem>
                    <SelectItem value="institution">Institution only</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={anonymous} onCheckedChange={setAnonymous} data-testid="help-anonymous" />
                <Label className="cursor-pointer">Give anonymously</Label>
              </div>
            </div>
            <Button className="w-full gap-1.5" onClick={submit} disabled={busy} data-testid="help-submit">
              <HandHeart size={16} weight="duotone" /> {busy ? "Sending…" : "Send my offer to help"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
